// src/components/practice/practiceHelpers.js
//
// Pure, framework-free helpers for the GENERIC practice components (PracticeQuiz /
// PracticeEssayView). Kept side-effect-free + JSX-free so they are unit-testable
// under `node --test` and reusable by set-text / skills / literature alike.

// D2: practice essays default to a 45-minute timed sitting.
export const DEFAULT_ESSAY_MINUTES = 45;

// Optional per-prompt timer override bounds (essay-prompt-minutes). MIRROR the backend
// (functions/src/schema/packSchema.js MIN/MAX_ESSAY_MINUTES) -- the two packages can't
// share a module. A prompt's content.minutes overrides the default only within these.
export const MIN_ESSAY_MINUTES = 5;
export const MAX_ESSAY_MINUTES = 180;

// The timer length (minutes) for a practice essay item: the prompt's own
// content.minutes when it is an integer within bounds, else the 45-minute default.
// So an untouched prompt (no/blank minutes) behaves exactly as before.
export function essayMinutesFor(item) {
    const m = item && item.content && item.content.minutes;
    return (Number.isInteger(m) && m >= MIN_ESSAY_MINUTES && m <= MAX_ESSAY_MINUTES) ? m : DEFAULT_ESSAY_MINUTES;
}

// Word count (matches the prototype: split on whitespace, drop empties).
export function countWords(text) {
    return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

// Seconds -> "m:ss" (minutes un-padded, seconds zero-padded), as the prototype timer.
export function formatClock(totalSeconds) {
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

// Deterministic auto-grade for one objective question (mcq / fill / matching),
// faithful to the prototype LitQuiz isQuestionCorrect. Pure -> testable.
//   mcq      answer = selected option index   -> Number(answer) === q.correct
//   fill     answer = typed string            -> trims+lowercases, matches ANY q.answers
//   matching answer = { leftIdx: rightIdx }   -> every left maps to its own right (answer[i] === i)
export function isQuestionCorrect(q, answer) {
    if (!q) return false;
    if (q.type === 'fill') {
        if (typeof answer !== 'string') return false;
        const norm = answer.trim().toLowerCase();
        return (q.answers || []).some((a) => String(a).trim().toLowerCase() === norm);
    }
    if (q.type === 'matching') {
        if (!answer || typeof answer !== 'object') return false;
        const pairs = q.pairs || [];
        return pairs.length > 0 && pairs.every((_, i) => answer[i] === i);
    }
    // mcq (default; also when type omitted)
    return answer != null && Number(answer) === q.correct;
}

// Whether an answer is COMPLETE enough to submit (enables the Check button).
export function isAnswerComplete(q, answer) {
    if (!q) return false;
    if (q.type === 'fill') return typeof answer === 'string' && answer.trim().length > 0;
    if (q.type === 'matching') {
        return !!answer && typeof answer === 'object' && (q.pairs || []).every((_, i) => answer[i] !== undefined);
    }
    return answer != null; // mcq
}

// ---- Essay-prompt parsing (Slice 4: past-paper-style layout) ----
// Turn an essay_prompt's flat `content.prompt` string into ordered display blocks so
// the reader can render the extract as a quote, the question as its own paragraph, and
// "Write about:" points as a list -- in NORMAL sentence case (the renderer uses a body
// font, not the all-caps display font). DEFENSIVE: unknown formats fall back to plain
// paragraph splitting; it never throws. Block kinds:
//   extract | question | instruction | list({items}) | para
//
// `toListItems`: split a chunk into bullet/numbered/multi-line items (markers stripped).
export function toListItems(chunk) {
    const lines = String(chunk == null ? '' : chunk).split('\n').map((l) => l.trim()).filter(Boolean);
    const hadMarkers = lines.some((l) => /^(?:[-*•–]|\d+[.)])\s+/.test(l));
    const stripped = lines.map((l) => l.replace(/^(?:[-*•–]|\d+[.)])\s+/, '').trim()).filter(Boolean);
    if (hadMarkers) return stripped;
    if (lines.length > 1) return stripped; // multi-line remainder (e.g. after "Write about:")
    return [];
}

export function parseEssayPrompt(raw) {
    const text = String(raw == null ? '' : raw).replace(/\r\n?/g, '\n').trim();
    if (!text) return [];
    const paras = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    const blocks = [];
    let questionSeen = false;
    for (const para of paras) {
        const writeAbout = /^write about\b[:.\s-]*/i.exec(para);
        if (writeAbout) {
            blocks.push({ kind: 'instruction', text: 'Write about:' });
            const items = toListItems(para.slice(writeAbout[0].length));
            if (items.length) blocks.push({ kind: 'list', items });
            questionSeen = true;
            continue;
        }
        const q = /^(?:essay\s+)?question\b\s*[:.]?\s*/i.exec(para);
        if (q) {
            const body = para.slice(q[0].length).trim();
            blocks.push({ kind: 'question', text: body || para });
            questionSeen = true;
            continue;
        }
        const items = toListItems(para);
        if (items.length >= 2 || (items.length === 1 && /^(?:[-*•–]|\d+[.)])\s+/.test(para))) {
            blocks.push({ kind: 'list', items });
            continue;
        }
        // Plain prose before any question reads as the extract; after, as a paragraph.
        blocks.push({ kind: questionSeen ? 'para' : 'extract', text: para });
    }
    // A lone block with no markers is just the question itself, not an extract/quote.
    if (blocks.length === 1 && blocks[0].kind === 'extract') {
        blocks[0] = { kind: 'question', text: blocks[0].text };
    }
    return blocks;
}

// Clamp a (possibly stale) card index against the CURRENT deck length. A mounted
// deck can shrink mid-session (e.g. an admin revokes an entitlement, the live
// entitlement listener flips, and paid items drop out of the deck); the flashcards
// reset effect keys on deck length and only runs AFTER the next render, so for one
// render a stale idx can exceed the new length. Every cards[idx] read must go
// through this clamp. Returns 0 for an empty deck or invalid input.
export function clampCardIndex(idx, length) {
    const len = Math.floor(Number(length) || 0);
    if (len <= 0) return 0;
    const i = Math.floor(Number(idx) || 0);
    return Math.min(Math.max(0, i), len - 1);
}

// In-session score: count answered questions that are correct (mixed types). Pure.
export function countCorrect(questions, answers) {
    const a = answers || {};
    let n = 0;
    (questions || []).forEach((q, i) => { if (a[i] != null && isQuestionCorrect(q, a[i])) n += 1; });
    return n;
}

// Stable per-question display shuffle; retain original answer indices and never
// expose the entire answer order by chance. Does not modify stored pairs.
export function shuffledMatchOptions(pairs, random = Math.random) {
    const options = (pairs || []).map((p, i) => ({ text: p.right, originalIdx: i }));
    for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]];
    }
    if (options.length > 1 && options.every((p, i) => p.originalIdx === i)) {
        options.push(options.shift());
    }
    return options;
}
